import { createApi } from '@reduxjs/toolkit/query/react';
import { getHeaders } from 'auth/auth';
import { baseQueryWithReauth } from '../baseQuery';

// RTK Query service for the FundMore Manager Portal rate-matrix integration.
// Backend: /api/v1/b2b/rate-matrix/* (read-only catalog) +
//          /api/v1/b2b/loans/{id}/rate-selection (writes to loan_applications).

export const rateMatrixAPI = createApi({
  reducerPath: 'rate-matrix',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['rateCatalog', 'rateSelection'],
  endpoints: (builder) => ({
    // ---- Catalog (read-only) ----
    getManagerPortalProducts: builder.query({
      query: () => ({
        url: '/b2b/rate-matrix/products',
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['rateCatalog'],
    }),

    getManagerPortalProduct: builder.query({
      query: ({ productId }) => ({
        url: `/b2b/rate-matrix/products/${productId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: (result, error, { productId }) => [
        { type: 'rateCatalog', id: `product-${productId}` },
      ],
    }),

    getManagerPortalRates: builder.query({
      query: () => ({
        url: '/b2b/rate-matrix/rates',
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['rateCatalog'],
    }),

    getManagerPortalCreditTiers: builder.query({
      query: () => ({
        url: '/b2b/rate-matrix/credit-tiers',
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['rateCatalog'],
    }),

    refreshManagerPortalCatalog: builder.mutation({
      query: () => ({
        url: '/b2b/rate-matrix/refresh',
        method: 'POST',
        headers: getHeaders(),
      }),
      invalidatesTags: ['rateCatalog'],
    }),

    // ---- Selection (writes to loan_applications) ----
    getRateSelection: builder.query({
      query: ({ applicationId }) => ({
        url: `/b2b/loans/${applicationId}/rate-selection`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: (result, error, { applicationId }) => [
        { type: 'rateSelection', id: applicationId },
      ],
    }),

    setRateSelection: builder.mutation({
      query: ({ applicationId, productId, rateId, creditTierId }) => ({
        url: `/b2b/loans/${applicationId}/rate-selection`,
        method: 'PUT',
        body: { productId, rateId, creditTierId },
        headers: getHeaders(),
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        { type: 'rateSelection', id: applicationId },
      ],
    }),

    clearRateSelection: builder.mutation({
      query: ({ applicationId }) => ({
        url: `/b2b/loans/${applicationId}/rate-selection`,
        method: 'DELETE',
        headers: getHeaders(),
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        { type: 'rateSelection', id: applicationId },
      ],
    }),

    setBaseRateOverride: builder.mutation({
      query: ({ applicationId, baseRate }) => ({
        url: `/b2b/loans/${applicationId}/base-rate`,
        method: 'PATCH',
        body: { baseRate },
        headers: getHeaders(),
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        { type: 'rateSelection', id: applicationId },
      ],
    }),

    setBuyDownRate: builder.mutation({
      query: ({ applicationId, buyDownRate }) => ({
        url: `/b2b/loans/${applicationId}/buy-down-rate`,
        method: 'PATCH',
        body: { buyDownRate },
        headers: getHeaders(),
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        { type: 'rateSelection', id: applicationId },
      ],
    }),
  }),
});

export const {
  useGetManagerPortalProductsQuery,
  useGetManagerPortalProductQuery,
  useGetManagerPortalRatesQuery,
  useGetManagerPortalCreditTiersQuery,
  useRefreshManagerPortalCatalogMutation,
  useGetRateSelectionQuery,
  useSetRateSelectionMutation,
  useClearRateSelectionMutation,
  useSetBaseRateOverrideMutation,
  useSetBuyDownRateMutation,
} = rateMatrixAPI;
